import type React from "react";
import { useId, useState } from "react";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const VIEW_HEIGHT = 146;
const RAY_ORIGIN = { x: 328, y: 101 };
const RAY_END_X = 410;

/** Gaussian-envelope wave packet: the ultrashort laser pulse on the optical bench. */
const PACKET_PATH = (() => {
	const points: string[] = [];
	for (let i = 0; i <= 360; i++) {
		const x = 50 + i * 0.5;
		const envelope = Math.exp(-(((x - 140) / 30) ** 2));
		const y = 96 - 13 * envelope * Math.sin((2 * Math.PI * (x - 140)) / 8);
		points.push(`${x.toFixed(1)} ${y.toFixed(2)}`);
	}
	return `M${points.join(" L")}`;
})();

const BENCH_TICKS = Array.from(
	{ length: 26 },
	(_, i) => `M${i * 10} 104 v${i % 5 === 0 ? 5 : 3}`,
).join(" ");

type RayKey = "product" | "ai" | "backend" | "web" | "mobile";

const RAYS: { id: RayKey; y: number; color: string; main?: boolean }[] = [
	{ id: "product", y: 22, color: "var(--scene-ray-1)" },
	{ id: "ai", y: 50, color: "var(--scene-ray-2)" },
	{ id: "backend", y: 78, color: "var(--scene-ray-3)" },
	{ id: "web", y: 106, color: "var(--scene-ray-4)" },
	{ id: "mobile", y: 134, color: "var(--scene-ray-5)", main: true },
];

const PRISM = "M300 46 L346 136 L254 136 Z";

/**
 * Hero scene: a femtosecond pulse travels along the bench, enters the "2020" prism
 * and is dispersed into the five domains. Pure CSS animation so it plays before
 * hydration, and settles on the full drawing under reduced motion.
 */
export function PrismScene({ className }: { className?: string }) {
	const { t } = useI18n();
	const uid = useId().replace(/:/g, "");
	const glassId = `${uid}-glass`;
	const dotsId = `${uid}-dots`;
	const [focus, setFocus] = useState<RayKey | null>(null);

	return (
		<figure
			className={cn("prism", className)}
			data-focus={focus ?? undefined}
			onMouseLeave={() => setFocus(null)}
		>
			<div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(13rem,15.5rem)] md:gap-3">
				<div className="relative">
					<figcaption className="mb-4 md:absolute md:top-0 md:left-0 md:mb-0">
						<span className="block font-mono text-[0.7rem] tracking-[0.08em] text-ink-3">
							{t((t) => t.scenes.prism.era)}
						</span>
						<span className="mt-1 block font-semibold text-ink">
							{t((t) => t.scenes.prism.title)}
						</span>
						<span className="block text-sm text-ink-2">
							{t((t) => t.scenes.prism.subtitle)}
						</span>
					</figcaption>
					<svg
						viewBox={`0 0 415 ${VIEW_HEIGHT}`}
						className="block h-auto w-full"
						aria-hidden="true"
					>
						<defs>
							<linearGradient id={glassId} x1="0" y1="0" x2="1" y2="1">
								<stop offset="0" style={{ stopColor: "var(--lilac)" }} />
								<stop offset="1" style={{ stopColor: "var(--sky)" }} />
							</linearGradient>
							<pattern
								id={dotsId}
								width="3.2"
								height="3.2"
								patternUnits="userSpaceOnUse"
							>
								<circle
									cx="1.6"
									cy="1.6"
									r=".55"
									opacity=".35"
									style={{ fill: "var(--ink)" }}
								/>
							</pattern>
						</defs>

						<path
							d={BENCH_TICKS}
							strokeWidth=".6"
							style={{ stroke: "var(--rule-2)" }}
						/>
						<line
							x1="0"
							y1="96"
							x2="274.4"
							y2="96"
							strokeWidth="1"
							style={{ stroke: "var(--ink)" }}
						/>
						<path
							className="prism-packet"
							d={PACKET_PATH}
							fill="none"
							strokeWidth="1"
							strokeLinejoin="round"
							style={{ stroke: "var(--ink)" }}
						/>

						<g fill="none" strokeLinecap="round">
							{RAYS.map((ray, i) => (
								<Ray key={ray.id} index={i} on={focus === ray.id} {...ray} />
							))}
						</g>

						<path d={PRISM} fill={`url(#${glassId})`} />
						<path d={PRISM} fill={`url(#${dotsId})`} />
						<path
							className="prism-flash"
							d={PRISM}
							style={{ fill: "var(--scene-card)" }}
						/>
						<path
							d={PRISM}
							fill="none"
							strokeWidth="1"
							strokeLinejoin="round"
							style={{ stroke: "var(--ink)" }}
						/>
						<path
							d={`M274.4 96 L${RAY_ORIGIN.x} ${RAY_ORIGIN.y}`}
							strokeWidth=".9"
							style={{ stroke: "var(--ink)" }}
						/>
						<text
							x="300"
							y="125"
							textAnchor="middle"
							fontFamily="IBM Plex Mono"
							fontSize="10"
							fontWeight="600"
							style={{ fill: "var(--ink)" }}
						>
							{t((t) => t.scenes.prism.pivot)}
						</text>
						{RAYS.map((ray) => (
							<circle
								key={ray.id}
								cx={RAY_END_X}
								cy={ray.y}
								r="1.8"
								style={{ fill: "var(--ink)" }}
							/>
						))}
					</svg>
				</div>

				<ul className="grid gap-3 md:relative md:block">
					{RAYS.map((ray, i) => (
						<li
							key={ray.id}
							data-on={focus === ray.id ? "" : undefined}
							onMouseEnter={() => setFocus(ray.id)}
							className="prism-label flex cursor-default items-baseline gap-2.5 md:absolute md:left-0 md:-translate-y-1/2"
							style={
								{
									"--i": i,
									top: `${(ray.y / VIEW_HEIGHT) * 100}%`,
								} as React.CSSProperties
							}
						>
							<span
								className="h-2 w-2 shrink-0 rounded-full md:hidden"
								style={{ background: ray.color }}
								aria-hidden="true"
							/>
							<span>
								<span className="block font-mono text-[0.68rem] uppercase tracking-[0.1em] text-ink-3">
									{t((t) => t.scenes.prism.rays[ray.id].label)}
								</span>
								<span
									className={cn(
										"prism-value block font-semibold text-ink",
										ray.main ? "text-base md:text-[1.05rem]" : "text-[0.95rem]",
									)}
								>
									{t((t) => t.scenes.prism.rays[ray.id].value)}
								</span>
							</span>
						</li>
					))}
				</ul>
			</div>
		</figure>
	);
}

function Ray({
	y,
	color,
	main,
	index,
	on,
}: {
	id: RayKey;
	y: number;
	color: string;
	main?: boolean;
	index: number;
	on: boolean;
}) {
	const line = {
		x1: RAY_ORIGIN.x,
		y1: RAY_ORIGIN.y,
		x2: RAY_END_X,
		y2: y,
		pathLength: 1,
	};
	const style = { "--i": index } as React.CSSProperties;

	return (
		<g className="prism-ray-group" data-on={on ? "" : undefined}>
			{main ? (
				<line
					{...line}
					className="prism-ray"
					strokeWidth="8"
					style={{ ...style, stroke: "var(--scene-ray-halo)" }}
				/>
			) : null}
			<line
				{...line}
				className="prism-ray"
				strokeWidth={main ? 2.4 : 1.3}
				style={{ ...style, stroke: color }}
			/>
			<line
				{...line}
				className="prism-glint"
				strokeWidth={main ? 3.4 : 2.4}
				style={{ ...style, stroke: color }}
			/>
			<line
				{...line}
				className="prism-beam"
				strokeWidth={main ? 3.4 : 2.6}
				style={{ stroke: color }}
			/>
		</g>
	);
}
